import { create } from "zustand";
import type { MapIntent } from "../types";

export interface PopupInfo {
  longitude: number;
  latitude: number;
  entityType: string;
  entityId: string;
  name: string;
  municipality: string;
}

export interface FlyToTarget {
  entityId: string;
  entityType: string;
  timestamp: number;
}

export interface CoordFlyTarget {
  longitude: number;
  latitude: number;
  zoom: number;
}

export interface ClickedLocation {
  latitude: number;
  longitude: number;
  name: string;
}

export interface DrawnPolygon {
  coordinates: [number, number][]; // vertices (open ring, NOT closed)
  name: string;
}

interface MapState {
  features: GeoJSON.FeatureCollection | null;
  viewport: { center: [number, number]; zoom: number } | null;
  pendingFeatures: GeoJSON.FeatureCollection | null;
  pendingViewport: { center: [number, number]; zoom: number } | null;
  selectedFeatureId: string | null;
  popupInfo: PopupInfo | null;
  userHasInteracted: boolean;
  flyToTarget: FlyToTarget | null;
  coordFlyTarget: CoordFlyTarget | null;
  clickedLocation: ClickedLocation | null;
  drawnPolygon: DrawnPolygon | null;
  isDrawing: boolean;
  /** Raw Mapbox GL map instance for direct queries (Streets v8 context extraction). */
  mapInstance: unknown | null;
  activePolygonIds: Set<string>;
  pinnedPolygonIds: Set<string>;
  hoveredEntityId: string | null;
  mapOverlayOpen: boolean;
  mapIntent: MapIntent | null;
  stagedFeatures: GeoJSON.FeatureCollection | null;
  revealedEntityIds: Set<string>;
  showAllResults: boolean;
  /** Background features from the last analysis turn — unrevealed entities persisted after turn_complete */
  backgroundFeatures: GeoJSON.FeatureCollection | null;

  setFeatures: (features: GeoJSON.FeatureCollection) => void;
  setViewport: (viewport: { center: [number, number]; zoom: number }) => void;
  setPendingFeatures: (features: GeoJSON.FeatureCollection) => void;
  setPendingViewport: (viewport: { center: [number, number]; zoom: number }) => void;
  commitPendingFeatures: () => void;
  clearPending: () => void;
  setSelectedFeatureId: (id: string | null) => void;
  setPopupInfo: (info: PopupInfo | null) => void;
  setUserHasInteracted: (value: boolean) => void;
  setFlyToTarget: (target: FlyToTarget | null) => void;
  setCoordFlyTarget: (target: CoordFlyTarget | null) => void;
  setClickedLocation: (loc: ClickedLocation | null) => void;
  clearClickedLocation: () => void;
  clearFeatures: () => void;
  activatePolygon: (entityId: string, pin?: boolean) => void;
  deactivatePolygon: (entityId: string) => void;
  clearActivePolygons: () => void;
  clearIsochrones: () => void;
  setDrawnPolygon: (polygon: DrawnPolygon | null) => void;
  clearDrawnPolygon: () => void;
  setIsDrawing: (value: boolean) => void;
  setMapInstance: (map: unknown | null) => void;
  setHoveredEntityId: (id: string | null) => void;
  setMapOverlayOpen: (open: boolean) => void;
  setMapIntent: (intent: MapIntent | null) => void;
  stageFeatures: (incoming: GeoJSON.FeatureCollection) => void;
  revealEntity: (entityId: string) => void;
  toggleShowAll: () => void;
  resetForNewTurn: () => void;
  commitRevealedFeatures: () => void;

  /** Find a feature by entity_id in the current features. Returns coords + props or null. */
  findFeature: (entityId: string) => {
    coordinates: [number, number];
    entityType: string;
    name: string;
    municipality: string;
  } | null;
}

export const useMapStore = create<MapState>((set, get) => ({
  features: null,
  viewport: null,
  pendingFeatures: null,
  pendingViewport: null,
  selectedFeatureId: null,
  popupInfo: null,
  userHasInteracted: false,
  flyToTarget: null,
  coordFlyTarget: null,
  clickedLocation: null,
  drawnPolygon: null,
  isDrawing: false,
  mapInstance: null,
  hoveredEntityId: null,
  mapOverlayOpen: false,
  activePolygonIds: new Set(),
  pinnedPolygonIds: new Set(),
  mapIntent: null,
  stagedFeatures: null,
  revealedEntityIds: new Set(),
  showAllResults: false,
  backgroundFeatures: null,

  setFeatures: (features) => set({ features }),
  setViewport: (viewport) => set({ viewport }),

  setPendingFeatures: (incoming) => set((state) => {
    if (!state.pendingFeatures) return { pendingFeatures: incoming };
    // Merge: dedup by entity_id + geometry type to keep both point and polygon
    const existingById = new Map(
      state.pendingFeatures.features.map((f) => [
        `${f.properties?.entity_id}:${f.geometry.type}`,
        f,
      ]),
    );
    for (const f of incoming.features) {
      existingById.set(`${f.properties?.entity_id}:${f.geometry.type}`, f);
    }
    return {
      pendingFeatures: {
        type: "FeatureCollection" as const,
        features: Array.from(existingById.values()),
      },
    };
  }),

  setPendingViewport: (viewport) => set({ pendingViewport: viewport }),

  commitPendingFeatures: () => set((state) => {
    if (!state.pendingFeatures) return {};
    return {
      features: state.pendingFeatures,
      viewport: state.pendingViewport ?? state.viewport,
      pendingFeatures: null,
      pendingViewport: null,
    };
  }),

  clearPending: () => set({ pendingFeatures: null, pendingViewport: null }),
  setSelectedFeatureId: (id) => set({ selectedFeatureId: id }),
  setPopupInfo: (info) => set({ popupInfo: info }),
  setUserHasInteracted: (value) => set({ userHasInteracted: value }),
  setFlyToTarget: (target) => set({ flyToTarget: target }),
  setCoordFlyTarget: (target) => set({ coordFlyTarget: target }),
  setClickedLocation: (loc) => set({ clickedLocation: loc, drawnPolygon: null }),
  clearClickedLocation: () => set({ clickedLocation: null }),
  clearFeatures: () =>
    set({ features: null, selectedFeatureId: null, popupInfo: null, userHasInteracted: false, flyToTarget: null, activePolygonIds: new Set(), pinnedPolygonIds: new Set(), drawnPolygon: null, isDrawing: false, backgroundFeatures: null }),

  activatePolygon: (entityId, pin = false) => set((state) => {
    const newActive = new Set<string>([entityId]);
    if (pin) {
      // Move current active to pinned, set new as active
      const newPinned = new Set(state.pinnedPolygonIds);
      for (const id of state.activePolygonIds) {
        newPinned.add(id);
      }
      return { activePolygonIds: newActive, pinnedPolygonIds: newPinned };
    }
    // Replace: clear active, keep pinned
    return { activePolygonIds: newActive };
  }),

  deactivatePolygon: (entityId) => set((state) => {
    const newActive = new Set(state.activePolygonIds);
    const newPinned = new Set(state.pinnedPolygonIds);
    newActive.delete(entityId);
    newPinned.delete(entityId);
    return { activePolygonIds: newActive, pinnedPolygonIds: newPinned };
  }),

  clearActivePolygons: () => set({ activePolygonIds: new Set(), pinnedPolygonIds: new Set() }),

  clearIsochrones: () => set((state) => {
    if (!state.features) return {};
    const filtered = state.features.features.filter(
      (f) => f.properties?.render_as !== "isochrone",
    );
    return {
      features: { type: "FeatureCollection" as const, features: filtered },
    };
  }),

  setDrawnPolygon: (polygon) => set({ drawnPolygon: polygon, clickedLocation: null, isDrawing: false }),
  clearDrawnPolygon: () => set({ drawnPolygon: null }),
  setIsDrawing: (value) => set({ isDrawing: value }),
  setMapInstance: (map) => set({ mapInstance: map }),
  setHoveredEntityId: (id) => set({ hoveredEntityId: id }),
  setMapOverlayOpen: (open) => set({ mapOverlayOpen: open }),

  setMapIntent: (intent) =>
    set({ mapIntent: intent }),

  stageFeatures: (incoming) =>
    set((state) => {
      const existing = state.stagedFeatures?.features ?? [];
      const incomingFeatures = incoming.features ?? [];
      // Merge by entity_id + geometry type (same dedup as setPendingFeatures)
      const byKey = new Map<string, GeoJSON.Feature>();
      for (const f of existing) {
        const key = `${f.properties?.entity_id}:${f.geometry.type}`;
        byKey.set(key, f);
      }
      for (const f of incomingFeatures) {
        const key = `${f.properties?.entity_id}:${f.geometry.type}`;
        byKey.set(key, f);
      }
      return {
        stagedFeatures: {
          type: "FeatureCollection" as const,
          features: Array.from(byKey.values()),
        },
      };
    }),

  revealEntity: (entityId) =>
    set((state) => {
      if (state.revealedEntityIds.has(entityId)) return {};
      const next = new Set(state.revealedEntityIds);
      next.add(entityId);
      return { revealedEntityIds: next };
    }),

  toggleShowAll: () =>
    set((state) => ({ showAllResults: !state.showAllResults })),

  resetForNewTurn: () =>
    set({
      mapIntent: null,
      stagedFeatures: null,
      revealedEntityIds: new Set(),
      showAllResults: false,
      backgroundFeatures: null,
    }),

  commitRevealedFeatures: () =>
    set((state) => {
      if (!state.stagedFeatures || state.revealedEntityIds.size === 0) return {};
      const revealed = state.stagedFeatures.features.filter(
        (f) => f.properties?.entity_id && state.revealedEntityIds.has(f.properties.entity_id)
      );
      const background = state.stagedFeatures.features.filter(
        (f) => f.properties?.entity_id && !state.revealedEntityIds.has(f.properties.entity_id)
      );
      const existing = state.features?.features ?? [];
      const byKey = new Map<string, GeoJSON.Feature>();
      for (const f of existing) {
        const key = `${f.properties?.entity_id}:${f.geometry.type}`;
        byKey.set(key, f);
      }
      for (const f of revealed) {
        const key = `${f.properties?.entity_id}:${f.geometry.type}`;
        byKey.set(key, f);
      }
      return {
        features: {
          type: "FeatureCollection" as const,
          features: Array.from(byKey.values()),
        },
        backgroundFeatures: background.length > 0
          ? { type: "FeatureCollection" as const, features: background }
          : null,
      };
    }),

  findFeature: (entityId) => {
    const state = get();
    // Search committed features first, then staged, then background
    const allFeats = [
      ...(state.features?.features ?? []),
      ...(state.stagedFeatures?.features ?? []),
      ...(state.backgroundFeatures?.features ?? []),
    ];
    if (allFeats.length === 0) return null;

    // Find a point feature (prefer points over polygons for centering)
    const point = allFeats.find(
      (f) => f.properties?.entity_id === entityId && f.geometry.type === "Point",
    );
    const feature = point ?? allFeats.find((f) => f.properties?.entity_id === entityId);
    if (!feature) return null;

    let coordinates: [number, number];
    if (feature.geometry.type === "Point") {
      coordinates = feature.geometry.coordinates as [number, number];
    } else if (feature.geometry.type === "Polygon") {
      // Use first coordinate as approximation
      const coords = feature.geometry.coordinates[0];
      if (coords && coords.length > 0) {
        const lngs = coords.map((c) => (c as number[])[0]!);
        const lats = coords.map((c) => (c as number[])[1]!);
        coordinates = [
          (Math.min(...lngs) + Math.max(...lngs)) / 2,
          (Math.min(...lats) + Math.max(...lats)) / 2,
        ];
      } else {
        return null;
      }
    } else {
      return null;
    }

    return {
      coordinates,
      entityType: (feature.properties?.entity_type as string) ?? "",
      name: (feature.properties?.name as string) ?? "",
      municipality: (feature.properties?.municipality as string) ?? "",
    };
  },
}));
